import cx from "classnames";
import { useTranslation } from "next-i18next";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

import { BreakoutWidth } from "@/components/BreakoutWidth";
import { Text } from "@/components/elements/Text";
import { isWebshopClosed } from "@/utils/helpers";

import styles from "./styles.module.css";

export type NotificationBannerProps = {
  breakout?: boolean;
  messages?: string[];
  delay?: number;
  display?: boolean;
  transitionDuration?: number;
  className?: string;
};

const defaultMessages = [
  "USP_banner_shipping",
  "USP_banner_plastic",
  "USP_banner_CO2",
];

export const NotificationBanner = ({
  breakout,
  messages = defaultMessages,
  delay = 3000,
  display = true,
  className,
}: NotificationBannerProps) => {
  const { t } = useTranslation();
  const Root = breakout ? BreakoutWidth : Fragment;
  // Loop through messages
  const timeout = useRef(null);
  const [messageIndex, setMessageIndex] = useState<number>(0);
  const [showMessage, setShowMessage] = useState(true);
  const message = messages[messageIndex];

  const advanceMessage = () => {
    if (isWebshopClosed) return;
    setMessageIndex((messageIndex) => (messageIndex + 1) % messages.length);
  };

  const getOnDone = (done, show) => () => {
    done();
    if (!show) {
      advanceMessage();
      setShowMessage(true);
      return;
    }
    timeout.current = setTimeout(() => {
      setShowMessage(false);
    }, delay);
  };

  useEffect(() => {
    // Do nothing if there one or fewer messages
    if (messages.length <= 1) return;
    // Start initial fade
    timeout.current = setTimeout(() => {
      setShowMessage(false);
    }, delay);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [delay, messages.length]);

  return (
    display && (
      <Root>
        <div
          className={cx(
            styles.container,
            { [styles.isClosed]: isWebshopClosed },
            className
          )}
        >
          <CSSTransition
            in={showMessage}
            addEndListener={(node, done) => {
              const onDone = getOnDone(done, showMessage);
              node.addEventListener("transitionend", onDone, {
                passive: true,
                once: true,
              });
            }}
            classNames={{
              enter: styles.enter,
              enterActive: styles.enterActive,
              exit: styles.exit,
              exitActive: styles.exitActive,
            }}
          >
            <div>
              <Text className={styles.copy}>{t(message)}</Text>
            </div>
          </CSSTransition>
        </div>
      </Root>
    )
  );
};
